/**
 * Minimal scene-graph surface for domain math.
 * PIXI.Container / PIXI.Sprite satisfy these structurally; domain never imports pixi.js.
 * Only fields actually read or written by domain modules are listed — no more.
 *
 * furnitureData stays `unknown` on purpose: builders (e.g. occupancy) only fill a
 * subset of RenderFurniture. Callers cast via readFurnitureData when they need
 * the full RenderFurniture shape — do NOT narrow to required RenderFurniture here.
 */
import type { RenderFurniture } from './types';

/**
 * Writable 2D point (PIXI.ObservablePoint is structurally compatible).
 * Domain math may assign `.x`/`.y`; scene drag code also calls `.set`.
 */
export interface ScenePosition {
  x: number;
  y: number;
  set(x: number, y: number): void;
}

/**
 * Child of a scene node (sprite-like). Domain reads geometry, scale (mirror),
 * tint/alpha (conflict clear), and furniture metadata bags.
 */
export interface SceneNodeChild {
  width: number;
  height: number;
  scale: { x: number };
  /** PIXI ColorSource at runtime; domain compares as number for conflict red. */
  tint?: unknown;
  alpha?: number;
  /** Bag hung on sprites/markers by the room renderer — shape not enforced here. */
  furnitureData?: unknown;
  /** Set by alpha-mask pass; used for rug footprint estimation. */
  __visualBaseOffsetX?: number;
  /** Set by alpha-mask pass; used for wall-mounted footprint estimation. */
  __maxOpaqueWidthPx?: number;
}

/**
 * Scene node used by placement / depth / carry math.
 * Fields verified against depthMath + surfaceCarry usage (visible is NOT used).
 */
export interface SceneNode {
  position: ScenePosition;
  zIndex: number;
  children: ReadonlyArray<SceneNodeChild>;
  /** Fallback when furnitureData is not on the first child. */
  furnitureData?: unknown;
}

/**
 * Read furniture metadata from child first, then marker fallback.
 * Single cast site so domain math never needs per-access furnitureData casts.
 */
export function readFurnitureData(
  marker: SceneNode,
  child?: SceneNodeChild | null,
): RenderFurniture | undefined {
  const raw = child?.furnitureData ?? marker.furnitureData;
  if (raw == null) return undefined;
  return raw as RenderFurniture;
}

/** Numeric tint for conflict checks (PIXI may expose ColorSource, not bare number). */
export function sceneTintNumber(tint: unknown): number {
  return typeof tint === 'number' ? tint : Number(tint);
}
